import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, Divider, IconButton,
  Stack, List, ListItem, ListItemText, CircularProgress, Tooltip, Alert,
  TextField, LinearProgress,
} from '@mui/material';
import {
  ArrowBack, Edit, OpenInNew, Description, CloudUpload, Delete,
  Inventory2, AttachFile, ShoppingCart, Schedule, Business, AssignmentTurnedIn,
} from '@mui/icons-material';
import api from '../../services/api';

const STATUS_COLOR = {
  draft: 'default', quote_requested: 'info', quoted: 'primary',
  negotiating: 'warning', ordered: 'secondary', shipped: 'info',
  received: 'success', cancelled: 'error',
};
const STATUS_LABEL = {
  draft: 'Draft', quote_requested: 'Quote Requested', quoted: 'Quoted',
  negotiating: 'Negotiating', ordered: 'Ordered', shipped: 'Shipped',
  received: 'Received', cancelled: 'Cancelled',
};
const PRIORITY_COLOR = { urgent: 'error', high: 'warning', medium: 'primary', low: 'default' };

const qty = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n/1024).toFixed(1)} KB`;
  return `${(n/(1024*1024)).toFixed(2)} MB`;
};

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>{label}</Typography>
    <Typography variant="body2" sx={{ mt: 0.3 }}>{value || '—'}</Typography>
  </Box>
);

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]     = useState(null);
  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileDescription, setFileDescription] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, f] = await Promise.all([
        api.get(`/sourcing-orders/${id}`),
        api.get(`/sourcing-orders/${id}/files`),
      ]);
      setOrder(o.data.data);
      setFiles(f.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load order');
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openFile = async (fileId) => {
    try {
      const res = await api.get(`/sourcing-orders/files/${fileId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { alert('Unable to open file'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (fileDescription) fd.append('description', fileDescription);
    try {
      await api.post(`/sourcing-orders/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const f = await api.get(`/sourcing-orders/${id}/files`);
      setFiles(f.data.data || []);
      setFileDescription('');
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Remove "${f.original_name}"?`)) return;
    await api.delete(`/sourcing-orders/files/${f.id}`);
    setFiles(files.filter(x => x.id !== f.id));
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!order) return null;

  const supplierName = order.beneficiary_name || order.sourcing_supplier_name;
  const supplierType = order.beneficiary_id ? 'Active Beneficiary' : order.sourcing_supplier_id ? 'Sourcing Candidate' : null;
  const supplierLink = order.beneficiary_id
    ? `/beneficiaries`
    : order.sourcing_supplier_id ? `/sourcing/discover/${order.sourcing_supplier_id}` : null;

  const totalQuoted = order.quoted_unit_price && order.quantity
    ? (parseFloat(order.quoted_unit_price) * parseFloat(order.quantity)).toFixed(2) : null;
  const totalTarget = order.target_unit_price && order.quantity
    ? (parseFloat(order.target_unit_price) * parseFloat(order.quantity)).toFixed(2) : null;

  const overdueDays = order.required_by && !['received','cancelled'].includes(order.status)
    ? Math.floor((new Date() - new Date(order.required_by)) / (1000 * 60 * 60 * 24)) : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/sourcing/orders')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5, gap: 1 }}>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{order.order_ref}</Typography>
            <Chip size="small" label={STATUS_LABEL[order.status] || order.status} color={STATUS_COLOR[order.status]} />
            <Chip size="small" label={order.priority?.toUpperCase()} color={PRIORITY_COLOR[order.priority]} />
            {overdueDays > 0 && <Chip size="small" label={`${overdueDays}d overdue`} color="error" />}
          </Stack>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 700 }}>{order.product_name}</Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<Edit />}
          onClick={() => navigate(`/sourcing/orders?edit=${id}`)}>Edit</Button>
      </Box>

      <Grid container spacing={2}>
        {/* Product details */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 fontSize="small" /> Product
            </Typography>
            <Field label="Description" value={order.product_description} />
            <Field label="Specifications" value={order.specifications} />
            <Grid container spacing={2}>
              <Grid item xs={6}><Field label="Quantity" value={`${qty(order.quantity)} ${order.unit_of_measure || ''}`} /></Grid>
              <Grid item xs={6}><Field label="UOM" value={order.unit_of_measure} /></Grid>
              <Grid item xs={6}><Field label="Target Price" value={order.target_unit_price ? `${order.target_currency} ${order.target_unit_price}` : null} /></Grid>
              <Grid item xs={6}><Field label="Target Total" value={totalTarget ? `${order.target_currency} ${parseFloat(totalTarget).toLocaleString()}` : null} /></Grid>
            </Grid>
          </Paper>

          {/* Supplier */}
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" /> Supplier
            </Typography>
            {supplierName ? (
              <Box>
                <Typography variant="body1" fontWeight={600}>{supplierName}</Typography>
                <Chip size="small" label={supplierType} variant="outlined" sx={{ mt: 0.5 }} />
                {supplierLink && order.sourcing_supplier_id && (
                  <Button size="small" sx={{ mt: 1, display: 'block' }}
                    onClick={() => navigate(supplierLink)}>View Supplier →</Button>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary">No supplier linked yet.</Typography>
            )}
          </Paper>
        </Grid>

        {/* Quote + Order */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule fontSize="small" /> Quote
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><Field label="Quoted Price" value={order.quoted_unit_price ? `${order.quoted_currency || order.target_currency} ${order.quoted_unit_price}` : null} /></Grid>
              <Grid item xs={6}><Field label="Quoted Total" value={totalQuoted ? `${order.quoted_currency || order.target_currency} ${parseFloat(totalQuoted).toLocaleString()}` : null} /></Grid>
              <Grid item xs={6}><Field label="MOQ" value={qty(order.quoted_moq)} /></Grid>
              <Grid item xs={6}><Field label="Lead Time" value={order.quoted_lead_time_days ? `${order.quoted_lead_time_days} days` : null} /></Grid>
              <Grid item xs={12}><Field label="Payment Terms" value={order.quoted_payment_terms} /></Grid>
              <Grid item xs={6}><Field label="Quote Date" value={order.quoted_at?.substring(0, 10)} /></Grid>
              <Grid item xs={6}><Field label="Required By" value={order.required_by?.substring(0, 10)} /></Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentTurnedIn fontSize="small" /> Order Placement
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}><Field label="PO Number" value={order.po_number} /></Grid>
              <Grid item xs={6}><Field label="Order Date" value={order.ordered_at?.substring(0, 10)} /></Grid>
              <Grid item xs={6}><Field label="Expected Delivery" value={order.expected_delivery?.substring(0, 10)} /></Grid>
              <Grid item xs={12}><Field label="Notes" value={order.notes} /></Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Attachments */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFile fontSize="small" /> Attachments ({files.length})
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField size="small" label="Description (optional)"
                  placeholder="Quote PDF, Spec sheet, PO copy, Sample photos…"
                  value={fileDescription} onChange={e => setFileDescription(e.target.value)} sx={{ flex: 1 }} />
                <Button component="label" variant="contained" startIcon={<CloudUpload />} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload File'}
                  <input type="file" hidden onChange={handleUpload} />
                </Button>
              </Stack>
              {uploading && <LinearProgress sx={{ mt: 1 }} />}
            </Paper>

            {files.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No files attached yet. Upload quotes, spec sheets, sample photos, or PO copies.
              </Typography>
            ) : (
              <List dense>
                {files.map(f => (
                  <ListItem key={f.id} divider
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Open"><IconButton size="small" onClick={() => openFile(f.id)}>
                          <OpenInNew fontSize="small" /></IconButton></Tooltip>
                        <IconButton size="small" color="error" onClick={() => deleteFile(f)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    }>
                    <Description sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={
                        <Box component="span" onClick={() => openFile(f.id)}
                          sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                          {f.original_name}
                        </Box>
                      }
                      secondary={`${f.description ? f.description + ' · ' : ''}${formatBytes(f.size_bytes)} · ${f.uploaded_by_name || 'Unknown'} · ${f.created_at?.substring(0, 10)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
